/**
 * Quote transport.
 *
 * A quote is a proposal: it moves no stock and posts no journal, which is why
 * it stays editable while an order does not. Four contract facts the screens
 * are built on:
 *
 *   • Totals are computed by the server from the lines. Nothing here sends a
 *     total, and nothing here recomputes one for display.
 *   • Each line's tax is determined by the same engine an invoice uses, so the
 *     figure quoted is the figure billed.
 *   • Only a `draft` or `sent` quote may be replaced. What a customer accepted
 *     is what they accepted.
 *   • Conversion is idempotent: the answer carries `created: false` when the
 *     quote had already become an order, and names that order either way.
 */
import type { components } from "@erp/api-client";

import { apiFetchOperational, type Page } from "@/lib/api/http";
import { fetchPage, type CursorQueryArgs, type ListFilters } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type QuoteStatus = Schemas["QuoteStatus"];
export type QuoteSummary = Schemas["QuoteSummary"];
export type Quote = Schemas["Quote"];
export type QuoteLine = Schemas["QuoteLine"];
export type QuoteWrite = Schemas["QuoteWrite"];
export type QuoteLineWrite = Schemas["QuoteLineWrite"];
export type QuoteConversion = Schemas["QuoteConversion"];
export type QuoteExpirationResult = Schemas["QuoteExpirationResult"];

export const QUOTE_STATUSES: readonly QuoteStatus[] = [
  "draft",
  "sent",
  "accepted",
  "declined",
  "expired",
  "converted",
];

/** What a quote in each state may do next — the same graph the database holds,
 *  so the screen offers exactly the moves that will be accepted. */
export const QUOTE_TRANSITIONS: Record<QuoteStatus, readonly QuoteStatus[]> = {
  draft: ["sent", "declined", "expired"],
  sent: ["accepted", "declined", "expired"],
  accepted: [],
  declined: [],
  expired: ["sent"],
  converted: [],
};

/** Editable only while it is a proposal. */
export function isQuoteEditable(status: QuoteStatus): boolean {
  return status === "draft" || status === "sent";
}

export interface QuoteFilters extends ListFilters {
  status?: QuoteStatus;
  customer_id?: string;
}

export function listQuotes(
  filters: QuoteFilters,
  args: CursorQueryArgs = {},
): Promise<Page<QuoteSummary>> {
  return fetchPage<QuoteSummary>("/quotes", { ...args, filters });
}

export function getQuote(quoteId: string, signal?: AbortSignal): Promise<Quote> {
  return apiFetchOperational<Quote>(`/quotes/${encodeURIComponent(quoteId)}`, { signal });
}

export function createQuote(body: QuoteWrite, idempotencyKey: string): Promise<Quote> {
  return apiFetchOperational<Quote>("/quotes", { method: "POST", body, idempotencyKey });
}

/** Replaces the terms and the lines whole; the number, branch and currency are
 *  fixed at creation and a different value is ignored rather than applied. */
export function replaceQuote(quoteId: string, body: QuoteWrite): Promise<Quote> {
  return apiFetchOperational<Quote>(`/quotes/${encodeURIComponent(quoteId)}`, {
    method: "PUT",
    body,
  });
}

export function transitionQuote(
  quoteId: string,
  toStatus: QuoteStatus,
  validUntil: string | undefined,
  idempotencyKey: string,
): Promise<Quote> {
  return apiFetchOperational<Quote>(`/quotes/${encodeURIComponent(quoteId)}/transitions`, {
    method: "POST",
    body: { to_status: toStatus, ...(validUntil === undefined ? {} : { valid_until: validUntil }) },
    idempotencyKey,
  });
}

export function convertQuote(quoteId: string, idempotencyKey: string): Promise<QuoteConversion> {
  return apiFetchOperational<QuoteConversion>(`/quotes/${encodeURIComponent(quoteId)}/conversion`, {
    method: "POST",
    idempotencyKey,
  });
}

/** Records which quotes have lapsed. Nothing expires by being read. */
export function expireQuotes(
  asOf: string | undefined,
  idempotencyKey: string,
): Promise<QuoteExpirationResult> {
  return apiFetchOperational<QuoteExpirationResult>("/quote-expirations", {
    method: "POST",
    body: asOf === undefined || asOf === "" ? {} : { as_of: asOf },
    idempotencyKey,
  });
}

/**
 * True when a quote's validity has passed while its stored status still says
 * otherwise. The screen shows this as "lapsed" rather than rewriting the
 * status it can see: only the sweep changes what the database holds.
 */
export function hasLapsed(quote: { status: QuoteStatus; valid_until: string }, today: string) {
  return (quote.status === "draft" || quote.status === "sent") && quote.valid_until < today;
}
